// src/pages/ActionItems.jsx
// 要対応 — 先生・教務が手を付けないといけないものを1か所に並べる【2026-11-02】
//   GET /api/dashboard/action-items（サーバーが、もとの一覧と同じ絞り込みで数える）
//   ここでは「何が何件あるか」と先頭の数件だけを見せ、処理はそれぞれの画面に任せる。
//   要注意のお知らせ（先生あて）だけは Web にほかの置き場所が無いので、ここで既読にできる。
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Notice from '../components/Notice';
import Icon from '../components/Icon';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token') });

/** バッジ（サイドバー）に「数が変わった」と知らせる */
export function notifyActionItemsChanged() {
  try { window.dispatchEvent(new Event('action-items-changed')); } catch (e) { /* 何もしない */ }
}

const SEVERITY = {
  critical: { label: '重大',   color: '#b91c1c', bg: '#fef2f2' },
  high:     { label: '要注意', color: '#c2410c', bg: '#fff7ed' },
  medium:   { label: '注意',   color: '#a16207', bg: '#fefce8' },
  low:      { label: '参考',   color: '#4b5563', bg: '#f3f4f6' },
};

function fmtDate(s) {
  if (!s) return '';
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${Number(m[2])}/${Number(m[3])}` : s;
}
function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default function ActionItems({ user }) {
  const role = user && user.role;
  const isTeacher = role === 'teacher';
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch('/api/dashboard/action-items', { headers: auth() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || j.error || '読み込めませんでした');
      setData(j);
    } catch (e) {
      setErr(e.message || '読み込めませんでした');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 60000);
    return () => clearInterval(t);
  }, [load]);

  // 行き先（先生は授業の「出欠の確認・修正」、教務は「出欠の確認」）
  const linkFor = (key, classId) => {
    if (key === 'requests' || key === 'pending') {
      if (isTeacher) return `/classes/${classId || 'all'}/edit?todo=${key}`;
      return `/requests?tab=${key}`;
    }
    if (key === 'merge') return '/students?tab=merge';
    if (key === 'alerts') return classId ? `/classes/${classId}/summary` : null;
    return null;
  };

  const markRead = async (id) => {
    setBusy(true);
    try {
      const r = await fetch(`/api/notifications/teacher-alerts/${id}/read`, { method: 'POST', headers: auth() });
      if (!r.ok) throw new Error('既読にできませんでした');
      await load();
      notifyActionItemsChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const markAllRead = async () => {
    setBusy(true);
    try {
      const r = await fetch('/api/notifications/teacher-alerts/read-all', { method: 'POST', headers: auth() });
      if (!r.ok) throw new Error('既読にできませんでした');
      await load();
      notifyActionItemsChanged();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const sections = (data && data.sections) || [];
  const total = data ? data.total : null;

  return (
    <div className="page-container todo-page">
      <div className="page-header">
        <h1>要対応</h1>
        <p>{isTeacher
          ? '担当の授業で、先生が確かめたり決めたりする必要があるものです。'
          : 'この大学で、教務が確かめたり決めたりする必要があるものです。'}
          {' '}1分ごとに新しくなります。</p>
      </div>

      <Notice kind="error">{err}</Notice>

      {data && total === 0 && (
        <div className="card todo-empty">
          <Icon name="check" size={22} />
          <span>いま対応が必要なものはありません</span>
        </div>
      )}
      {data && data.failed > 0 && (
        <Notice kind="warn">一部の項目を読み込めませんでした。しばらくしてから開き直してください</Notice>
      )}
      {!data && !err && <div className="card" style={{ color: '#6b7280' }}>読み込み中…</div>}

      {sections.filter(s => s.count > 0).map(s => (
        <div key={s.key} className={'card todo-section todo-' + s.key}>
          <div className="todo-head">
            <h2>{s.title}</h2>
            <span className="todo-count">{s.count}{s.countCapped ? '+' : ''}件</span>
            {s.note && <span className="todo-note">{s.note}</span>}
            <span style={{ flex: 1 }} />
            {s.key === 'alerts' ? (
              <button type="button" className="btn-secondary todo-btn" disabled={busy} onClick={markAllRead}>
                すべて既読にする
              </button>
            ) : (() => {
              // 授業が1つだけなら、その授業へ直接
              const one = isTeacher && s.byClass && s.byClass.length === 1 ? s.byClass[0] : null;
              const to = linkFor(s.key, one ? one.classId : undefined);
              if (!to) return null;
              return (
                <Link className="btn-primary todo-btn" to={to}>
                  {s.key === 'merge' ? '統合の画面へ'
                    : one ? `「${one.className}」で確認する`
                    : (isTeacher ? 'すべての授業でまとめて見る' : '一覧へ')}
                </Link>
              );
            })()}
          </div>

          {/* 授業ごとの件数（先生の申請・確認待ち） */}
          {isTeacher && s.byClass && s.byClass.length > 1 && (
            <div className="todo-classes">
              {s.byClass.map(c => (
                <Link key={c.classId} to={linkFor(s.key, c.classId)} className="todo-class-chip">
                  {c.className} <b>{c.count}</b>
                </Link>
              ))}
            </div>
          )}

          {s.items && s.items.length > 0 && (
            <ul className="todo-items">
              {s.items.map(it => {
                const sev = s.key === 'alerts' ? (SEVERITY[it.severity] || SEVERITY.low) : null;
                const to = linkFor(s.key, it.classId);
                const main = (
                  <>
                    {sev && <span className="todo-sev" style={{ color: sev.color, background: sev.bg }}>{sev.label}</span>}
                    <span className="todo-item-main">
                      {s.key === 'alerts' ? it.title : (it.studentName || it.studentId || '')}
                      {s.key !== 'alerts' && it.studentId && it.studentName && <small> {it.studentId}</small>}
                    </span>
                    <span className="todo-item-sub">
                      {[it.className,
                        it.sessionDate ? fmtDate(it.sessionDate) + (it.sessionNo ? `（第${it.sessionNo}回）` : '') : null,
                        s.key === 'alerts' ? (it.studentName || it.studentId) : it.label,
                        it.statusLabel && it.status === 'needs_info' ? it.statusLabel : null,
                      ].filter(Boolean).join('　')}
                    </span>
                    <span className="todo-item-when">{fmtWhen(it.createdAt || it.recordedAt)}</span>
                  </>
                );
                return (
                  <li key={it.id}>
                    {to ? <Link to={to} className="todo-item">{main}</Link> : <div className="todo-item">{main}</div>}
                    {s.key === 'alerts' && (
                      <button type="button" className="btn-secondary todo-read" disabled={busy}
                              onClick={() => markRead(it.id)}>既読</button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {s.items && s.count > s.items.length && s.key !== 'alerts' && (
            <div className="todo-more">ほか {s.count - s.items.length}件。ボタンから一覧を開いてください</div>
          )}
        </div>
      ))}
    </div>
  );
}
